import React, { useEffect, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import _ from 'lodash';
import MainCard from './MainCard';
import { Grid, Typography } from '@ts/storybook';
import { IChartOptions, IDataChart } from '@ts/ui-models';

import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material';
import { useTheme } from '@mui/material';
import { Stack, Box, Skeleton } from '@mui/material';

interface BarChartProp extends WithTranslation {
  dataChart?: IDataChart;
  options?: IChartOptions;
  loading: boolean;

  isMock?: boolean;
  type?: number; //Only for a mock
}

// Chart options
const barChartOptions: ApexOptions = {
  chart: {
    type: 'bar',
    toolbar: {
      show: false,
    },
    animations: {
      enabled: true,
      //easing: 'easeinout',
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 1500,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 700,
      },
    },
  },
  plotOptions: {
    bar: {
      columnWidth: '45%',
      borderRadius: 4,
    },
  },
  dataLabels: {
    enabled: true,
  },
  xaxis: {},
  yaxis: {
    show: false,
  },
  grid: {
    show: false,
  },
  tooltip: {
    theme: 'light',
    x: {
      formatter: (val: number) => {
        return val.toString();
      },
    },
  },
};

const mockQuantityData = (): IDataChart => {
  return {
    xaxis: {
      categories: ['Trucks', 'Pallets', 'Containers', 'Forklifts', 'Laptops', 'Workers'],
    },
    series: [
      {
        name: 'Quantity',
        data: [8, 30, 25, 6, 20, 20],
      },
    ],
  };
};

const mockQuantityData1 = (): IDataChart => {
  return {
    xaxis: {
      categories: ['Earth moving equipment', 'Trucks', 'HD, Manlift, tippers', 'Cranes', 'Generators'],
    },
    series: [
      {
        name: 'PERSONNEL',
        data: [15, 7, 23, 2, 6],
      },
    ],
  };
};
const useStyles = makeStyles<Theme, { theme: Theme }>(() => ({
  positionRotatedText: { position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  rotatedText: props => ({
    color: props.theme.palette.text.disabled,
    textAlign: 'center',
    transform: 'rotate(-45deg)', // Elige el ángulo de rotación deseado (en grados)
  }),
  noData: {
    backgroundColor: '#c4bfbf29',
  },
}));

const BarChart: React.FC<BarChartProp> = props => {
  const t = (key: string) => props.t(key).toString();
  const { color, title, withoutTotal, height = 400 } = props.options || {};
  //#region Styles
  const theme = useTheme();
  const classesStyle = useStyles({ theme });

  //const primary = theme.palette.primary.main;
  const primary = '#6e51cf';
  //#endregion

  const mockData = props.type === 1 ? mockQuantityData() : mockQuantityData1();

  const [series, setSeries] = useState(props.isMock ? mockData.series : props.dataChart?.series || []);
  const [options, setOptions] = useState<ApexOptions>(barChartOptions);

  //#region Functions
  const fillOptionsChart = (prevState: any) => {
    return {
      ...prevState,
      colors: Array.isArray(color) ? color : [color || primary],
      xaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        categories: props.isMock ? mockData.xaxis?.categories : props.dataChart?.xaxis?.categories || [],
        labels: {
          rotate: -60,
          formatter: (val: string) => {
            return val.length > 20 ? val.substring(0, 20) + '...' : val;
          },
          /* style: {
            colors: [secondary, secondary, secondary, secondary, secondary, secondary, secondary],
          }, */
        },
      },
    };
  };
  const getData = () => {
    // Verificar si series es una lista de objetos
    if (Array.isArray(series) && typeof series[0] === 'number') {
      return series;
    } else if (Array.isArray(series) && typeof series[0] === 'object') {
      return series[0].data;
    }
  };
  const checkArrayAllZeros = (array: any) => {
    if (array) {
      for (let i = 0; i < array.length; i++) {
        if (array[i] !== 0) {
          return false;
        }
      }
      return true;
    }
    return false;
  };
  const checkEmptyData = () => {
    if (props.isMock) return false;
    if (checkArrayAllZeros(getData())) return true;
    return !getData() || getData()?.length === 0;
  };
  const getChartWidth = () => {
    return (
      ((props.isMock ? mockData.xaxis?.categories?.length : props.dataChart?.xaxis?.categories?.length) || 0) *
      (((props.isMock ? mockData.xaxis?.categories?.length : props.dataChart?.xaxis?.categories?.length) || 0) > 10 ? 50 : 70)
    );
  };
  //#endregion

  useEffect(() => {
    setOptions(prevState => fillOptionsChart(prevState));
    setSeries(props.isMock ? mockData.series : props.dataChart?.series || []);
  }, [props.dataChart]);

  if (props.loading)
    return (
      <MainCard contentSX={{ p: 2.25 }}>
        <Stack spacing={0.5}>
          <Grid container alignItems="center">
            <Grid size={7}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
            <Grid size={1}></Grid>
            <Grid size={4}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
          </Grid>
        </Stack>
        <Box sx={{ pt: 2.75 }}>
          <Skeleton variant="rounded" height={420} />
        </Box>
      </MainCard>
    );

  if (checkEmptyData()) return null;

  return (
    <MainCard contentSX={{ p: 2.25, pb: '0!important' }}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid>{title && <Typography variant="h5" fontWeight="500" color="primary" text={title} />}</Grid>
        <Grid>
          {!withoutTotal && (
            <Typography
              variant="h6"
              fontWeight="500"
              textAlign="end"
              // @ts-ignore
              text={['Total', ': ', _.sum(props.isMock ? mockData.series[0].data : props.dataChart?.series[0].data)]}
            />
          )}
        </Grid>
      </Grid>

      <div style={{ overflow: 'auto', position: 'relative' }} className={checkEmptyData() ? classesStyle.noData : ''}>
        <div style={{ width: getChartWidth(), minWidth: '100%', minHeight: height + 20 + 'px' }}>
          {checkEmptyData() ? (
            <div className={classesStyle.positionRotatedText}>
              <Typography variant="h3" className={classesStyle.rotatedText} text="NO DATA" />
            </div>
          ) : null}
          <ReactApexChart
            options={options}
            series={series}
            // @ts-ignore
            type={barChartOptions.chart?.type}
            height={height + 'px'}
          />
        </div>
      </div>
    </MainCard>
  );
};

export default withTranslation()(BarChart);
